import { database } from "../firebase/config";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  addDoc,
  where,
  deleteDoc,
} from "firebase/firestore";
import { getEventSongsData } from "../data/songsData";

export const votingService = {
  // Invia un singolo voto per una canzone
  async submitSingleVote(
    userId,
    songId,
    songVotes,
    userIdentifier,
    nightId,
    eventId = "sanremo",
  ) {
    try {
      const voteData = {
        eventId,
        userId,
        idSong: songId,
        votes: songVotes,
        timestamp: new Date(),
        userIdentifier: userIdentifier || "",
        nightId: nightId || 1,
      };

      // Gestione specifica per la quarta serata (cover)
      if (songVotes["canzone_cover"] !== undefined) {
        voteData.coverVote = songVotes["canzone_cover"];
      }

      // Prima elimina eventuali voti precedenti per questa canzone e serata
      await this.deletePreviousVote(userId, songId, nightId, eventId);

      // Salva il nuovo voto usando addDoc per evitare problemi di permessi
      const docRef = await addDoc(collection(database, "votes"), voteData);

      console.log("Voto salvato con ID:", docRef.id);
      return { ...voteData, id: docRef.id };
    } catch (error) {
      console.error("Errore nel salvataggio del voto:", error);
      throw new Error(`Errore nel salvataggio: ${error.message}`);
    }
  },

  // Elimina voti precedenti per una specifica canzone
  async deletePreviousVote(userId, songId, nightId, eventId = "sanremo") {
    try {
      const baseConstraints = [
        where("userId", "==", userId),
        where("idSong", "==", songId),
      ];

      if (nightId) {
        baseConstraints.push(where("nightId", "==", nightId));
      }

      const constraints =
        eventId === "sanremo"
          ? baseConstraints
          : [...baseConstraints, where("eventId", "==", eventId)];

      const votesQuery = query(collection(database, "votes"), ...constraints);

      const querySnapshot = await getDocs(votesQuery);
      const deletePromises = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (
          eventId === "sanremo" &&
          data.eventId !== undefined &&
          data.eventId !== "sanremo"
        ) {
          return;
        }
        deletePromises.push(deleteDoc(doc.ref));
      });

      await Promise.all(deletePromises);
      console.log(
        `Eliminati ${deletePromises.length} voti precedenti per ${songId} (serata ${nightId})`,
      );
    } catch (error) {
      console.warn("Errore nell'eliminazione voti precedenti:", error.message);
      // Non bloccare il salvataggio se l'eliminazione fallisce
    }
  },

  // Finalizza la votazione (imposta hasVoted a true)
  async finalizeVoting(userId, userIdentifier) {
    try {
      const userDocRef = doc(database, "users", userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          hasVoted: true,
          lastVoteTime: new Date(),
          identifier: userIdentifier || "",
        });
      } else {
        await setDoc(userDocRef, {
          identifier: userIdentifier || "",
          hasVoted: true,
          lastVoteTime: new Date(),
          createdAt: new Date(),
        });
      }
      return { success: true };
    } catch (error) {
      console.error("Errore nella finalizzazione:", error);
      throw new Error(`Errore nella finalizzazione: ${error.message}`);
    }
  },

  // Invia tutti i voti (per compatibilità)
  async submitVotes(userId, allVotes, eventId = "sanremo") {
    try {
      const userIdentifier =
        allVotes.userIdentifier || allVotes.userEmail || "";
      const { songs: songsData, categories: categoriesData } =
        getEventSongsData(eventId);
      const songs = songsData.map((s) => s.key);
      const categories = categoriesData.map((c) => c.key);

      // Raggruppa i voti per canzone
      const votePromises = [];

      songs.forEach((songId) => {
        const songVotes = {};
        let hasSongVotes = false;

        categories.forEach((category) => {
          const voteKey = `${songId}_${category}`;
          if (allVotes[voteKey] !== undefined) {
            songVotes[category] = allVotes[voteKey];
            hasSongVotes = true;
          }
        });

        if (hasSongVotes) {
          votePromises.push(
            this.submitSingleVote(
              userId,
              songId,
              songVotes,
              userIdentifier,
              undefined,
              eventId,
            ),
          );
        }
      });

      await Promise.all(votePromises);

      // Verifica se il documento utente esiste, altrimenti crealo
      const userDocRef = doc(database, "users", userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // Aggiorna lo stato hasVoted dell'utente esistente
        await updateDoc(userDocRef, {
          hasVoted: true,
          lastVoteTime: new Date(),
        });
      } else {
        // Crea il documento utente se non esiste
        await setDoc(userDocRef, {
          identifier: userIdentifier,
          hasVoted: true,
          lastVoteTime: new Date(),
          createdAt: new Date(),
        });
      }

      return { success: true, votesCount: votePromises.length };
    } catch (error) {
      throw new Error(error.message);
    }
  },

  // Ottieni voti di un utente
  async getUserVotes(userId, eventId = "sanremo") {
    try {
      const votesQuery = query(
        collection(database, "votes"),
        orderBy("timestamp", "desc"),
      );
      const querySnapshot = await getDocs(votesQuery);

      const userVotes = {};
      let hasVotes = false;
      let userIdentifier = "";
      let timestamp = null;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const isMatchingEvent =
          eventId === "sanremo"
            ? data.eventId === undefined || data.eventId === "sanremo"
            : data.eventId === eventId;

        if (data.userId === userId && isMatchingEvent) {
          hasVotes = true;
          userIdentifier = data.userIdentifier || data.userEmail || "";
          if (!timestamp || data.timestamp > timestamp) {
            timestamp = data.timestamp;
          }

          // Ricostruisce la struttura originale dei voti
          const nightId = data.nightId || 1;
          Object.keys(data.votes).forEach((category) => {
            if (category === "canzone") {
              const voteKey = `${data.idSong}_${category}`;
              // Se esiste già, significa che abbiamo trovato un voto più recente (grazie all'ordinamento),
              // ma siccome stiamo iterando su tutti i voti, e l'ordine è desc, il primo che troviamo è il più recente.
              // Quindi se NON esiste, lo settiamo.
              if (userVotes[voteKey] === undefined) {
                userVotes[voteKey] = data.votes[category];
              }
            } else {
              const voteKey = `${data.idSong}_${nightId}_${category}`;
              userVotes[voteKey] = data.votes[category];
            }
          });
        }
      });

      if (hasVotes) {
        return {
          userId,
          eventId,
          votes: userVotes,
          userIdentifier,
          timestamp,
        };
      }

      return null;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  // Ottieni tutti i voti per i risultati
  async getAllVotes(eventId = "sanremo") {
    try {
      const votesQuery = query(
        collection(database, "votes"),
        orderBy("timestamp", "desc"),
      );
      const querySnapshot = await getDocs(votesQuery);

      const votes = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const isMatchingEvent =
          eventId === "sanremo"
            ? data.eventId === undefined || data.eventId === "sanremo"
            : data.eventId === eventId;

        if (isMatchingEvent) {
          votes.push({ id: doc.id, ...data });
        }
      });

      return votes;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  // Calcola statistiche aggregate per la nuova struttura di voti
  async getAggregatedResults(eventId = "sanremo") {
    try {
      const allVotes = await this.getAllVotes(eventId);

      const { songs: songsData, categories: categoriesData } =
        getEventSongsData(eventId);
      const songs = songsData.map((s) => s.key);
      const categories = categoriesData.map((c) => c.key);

      // Conta gli utenti unici per calcolare correttamente le medie
      const uniqueUsers = new Set(allVotes.map((vote) => vote.userId));
      const totalUsers = uniqueUsers.size;

      const results = {
        totalVotes: allVotes.length,
        totalUsers: totalUsers,
        songTotals: {},
        songAverages: {},
        categoryTotals: {},
        categoryAverages: {},
        songCategoryScores: {},
        distribution: {},
        rankings: {
          overall: [],
          byCategory: {},
        },
      };

      // Inizializza le strutture dati
      songs.forEach((song) => {
        results.songTotals[song] = 0;
        results.songCategoryScores[song] = {};
        categories.forEach((category) => {
          results.songCategoryScores[song][category] = {
            total: 0,
            count: 0,
            average: 0,
          };
        });
      });

      categories.forEach((category) => {
        results.categoryTotals[category] = 0;
        results.distribution[category] = {
          1: 0,
          2: 0,
          3: 0,
          4: 0,
          5: 0,
          6: 0,
          7: 0,
          8: 0,
          9: 0,
          10: 0,
        };
        results.rankings.byCategory[category] = [];
      });

      // Processa i voti per utente/canzone prima di aggregarli
      const userSongVotes = {}; // { userId: { songId: { category: [scores] } } }

      allVotes.forEach((vote) => {
        const { userId, idSong, votes: voteData } = vote;

        if (!songs.includes(idSong)) return;

        if (!userSongVotes[userId]) {
          userSongVotes[userId] = {};
        }
        if (!userSongVotes[userId][idSong]) {
          userSongVotes[userId][idSong] = {};
        }

        Object.keys(voteData).forEach((category) => {
          if (!userSongVotes[userId][idSong][category]) {
            userSongVotes[userId][idSong][category] = [];
          }
          // Per la categoria "canzone", prendiamo solo il voto più recente (il primo trovato dato l'ordinamento desc)
          if (category === "canzone") {
            if (userSongVotes[userId][idSong][category].length === 0) {
              userSongVotes[userId][idSong][category].push(voteData[category]);
            }
          } else {
            // Per le altre categorie, raccogliamo tutti i voti delle varie serate
            userSongVotes[userId][idSong][category].push(voteData[category]);
          }
        });
      });

      // Calcola totali e distribuzione basandosi sui voti processati (media per utente)
      Object.keys(userSongVotes).forEach((userId) => {
        const userSongs = userSongVotes[userId];

        Object.keys(userSongs).forEach((songId) => {
          let songTotal = 0;

          Object.keys(userSongs[songId]).forEach((category) => {
            const scores = userSongs[songId][category];
            if (scores.length === 0) return;

            // Calcola il punteggio effettivo per questo utente/canzone/categoria
            let effectiveScore;

            if (category === "canzone") {
              effectiveScore = scores[0]; // Già filtrato per essere l'ultimo
            } else {
              // Media delle serate
              const sum = scores.reduce((a, b) => a + b, 0);
              effectiveScore = sum / scores.length;
            }

            // Arrotonda per la distribuzione, ma usa il valore preciso per i totali
            const roundedScore = Math.round(effectiveScore);

            if (effectiveScore >= 1 && effectiveScore <= 10) {
              results.songCategoryScores[songId][category].total +=
                effectiveScore;
              results.songCategoryScores[songId][category].count++;
              results.categoryTotals[category] += effectiveScore;

              if (roundedScore >= 1 && roundedScore <= 10) {
                results.distribution[category][roundedScore]++;
              }

              songTotal += effectiveScore;
            }
          });

          results.songTotals[songId] += songTotal;
        });
      });

      // Calcola medie per canzoni e categorie
      songs.forEach((song) => {
        const songVoteCount = results.songCategoryScores[song]["canzone"].count;
        results.songAverages[song] =
          songVoteCount > 0
            ? (results.songTotals[song] / songVoteCount).toFixed(2)
            : 0;

        categories.forEach((category) => {
          const categoryData = results.songCategoryScores[song][category];
          categoryData.average =
            categoryData.count > 0
              ? (categoryData.total / categoryData.count).toFixed(2)
              : 0;
        });
      });

      categories.forEach((category) => {
        // Count users who voted for this category, not just raw votes
        const usersVotedForCategory = Object.values(userSongVotes).filter(
          (userSongs) =>
            Object.values(userSongs).some(
              (song) => song[category] && song[category].length > 0,
            ),
        ).length;

        results.categoryAverages[category] =
          usersVotedForCategory > 0
            ? (results.categoryTotals[category] / usersVotedForCategory) // This might be slightly off if total is sum of averages across songs. Wait.
                // Actually categoryAverage is usually (Sum of all scores) / (Total number of scores).
                // Here "scores" are now "user-song-averaged-scores".
                // So we need to count how many "user-song-averaged-scores" contributed to categoryTotals[category].
                // Let's recalculate the count during the iteration above.
                // Current implementation of results.categoryTotals[category] is correct (sum of effective scores).
                // We need the count of effective scores added.
                // results.songCategoryScores[songId][category].count stores exactly that (number of users who voted for song/category).
                // So we can sum those up.
                .toFixed(2)
            : 0;
      });

      // Recalculate category averages properly
      categories.forEach((category) => {
        let totalCount = 0;
        songs.forEach((song) => {
          totalCount += results.songCategoryScores[song][category].count;
        });

        results.categoryAverages[category] =
          totalCount > 0
            ? (results.categoryTotals[category] / totalCount).toFixed(2)
            : 0;
      });

      // Genera classifiche
      results.rankings.overall = songs
        .map((song) => ({
          song,
          total: parseFloat(results.songTotals[song]),
          average: parseFloat(results.songAverages[song]),
          voteCount: results.songCategoryScores[song]["canzone"].count,
        }))
        .sort((a, b) => b.average - a.average);

      categories.forEach((category) => {
        results.rankings.byCategory[category] = songs
          .map((song) => ({
            song,
            average: parseFloat(
              results.songCategoryScores[song][category].average,
            ),
            total: results.songCategoryScores[song][category].total,
            count: results.songCategoryScores[song][category].count,
          }))
          .sort((a, b) => b.average - a.average);
      });

      return results;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  // Nuova funzione per ottenere statistiche live
  async getLiveStats(eventId = "sanremo") {
    try {
      const allVotes = await this.getAllVotes(eventId);
      const results = await this.getAggregatedResults(eventId);

      // Calcola statistiche interessanti
      const stats = {
        totalVotes: allVotes.length,
        lastVoteTime: allVotes.length > 0 ? allVotes[0].timestamp : null,
        votingTrend: this.calculateVotingTrend(allVotes),
        categoryPopularity: this.calculateCategoryPopularity(results),
        userEngagement: this.calculateUserEngagement(allVotes),
      };

      return stats;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  // Funzioni di supporto per statistiche avanzate
  calculateVotingTrend(votes) {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    const recentVotes = votes.filter(
      (vote) => vote.timestamp && vote.timestamp.toDate() > oneHourAgo,
    );
    return {
      lastHour: recentVotes.length,
      trend: recentVotes.length > votes.length * 0.1 ? "increasing" : "stable",
    };
  },

  calculateCategoryPopularity(results) {
    const categories = ["canzone", "presenza_scenica", "esibizione"];
    return categories
      .map((category) => ({
        category,
        average: parseFloat(results.categoryAverages[category]),
        highScores: Object.values(results.distribution[category])
          .slice(7)
          .reduce((a, b) => a + b, 0),
      }))
      .sort((a, b) => b.average - a.average);
  },

  calculateUserEngagement(votes) {
    const totalPossibleVotes = votes.length * 26 * 3; // 26 canzoni * 3 categorie
    const actualVotes = votes.reduce((total, vote) => {
      return total + Object.keys(vote.votes).length;
    }, 0);

    return {
      completionRate:
        totalPossibleVotes > 0
          ? ((actualVotes / totalPossibleVotes) * 100).toFixed(1)
          : 0,
      averageVotesPerUser:
        votes.length > 0 ? (actualVotes / votes.length).toFixed(1) : 0,
    };
  },
};
